import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import { AnimatedHeaderScreen } from "../AnimatedHeaderScreen";
import Box from "../Box";
import Container from "../Container";
import CustomTabs from "../CustomTabs";
import { LessonDetailMetaChips } from "./LessonDetailMetaChips";
import { LessonDetailProgress } from "./LessonDetailProgress";
import { LessonDetailTitleSection } from "./LessonDetailTitleSection";
import type { LessonDetailMetaChip, LessonDetailTab } from "./types";

type Props = {
	title: string;
	description: string;
	progressPercent: number;
	tabs: LessonDetailTab[];
	initialTabIndex?: number;
	metaChips?: LessonDetailMetaChip[];
	coverImage?: string;
	onBackPress: () => void;
};

export const LessonDetailViewScreen = ({
	title,
	description,
	progressPercent,
	tabs,
	initialTabIndex,
	metaChips,
	coverImage,
	onBackPress,
}: Props) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const { t } = useTranslation();
	const [activeTabIndex, setActiveTabIndex] = useState(initialTabIndex ?? 0);

	const containedTabs = tabs.map((tab) => ({
		...tab,
		content: <Container maxWidth="sm">{tab.content}</Container>,
	}));

	return (
		<AnimatedHeaderScreen
			title={title}
			coverImage={coverImage}
			fullBleedContent
			solidHeaderOnMobile
			leadingAction={{
				icon: "arrow-left",
				accessibilityLabel: t("common.goBack"),
				onPress: onBackPress,
			}}
			renderHero={({ invertColors, onTitleLayout }) => (
				<Box gap={14} paddingHorizontal={isMobile ? 24 : 0}>
					{metaChips?.length ? (
						<LessonDetailMetaChips
							chips={metaChips}
							invertColors={invertColors}
						/>
					) : null}
					<LessonDetailTitleSection
						title={title}
						description={description}
						invertColors={invertColors}
						titleVariant={isMobile ? "headlineSmall" : "headlineLarge"}
						onTitleLayout={onTitleLayout}
					/>
					<LessonDetailProgress
						percent={progressPercent}
						invertColors={invertColors}
					/>
				</Box>
			)}
			bottomBar={isMobile ? tabs[activeTabIndex]?.bottomBar : undefined}
		>
			<CustomTabs
				tabs={containedTabs}
				mode="fixed"
				uppercase={false}
				iconPosition="leading"
				defaultIndex={initialTabIndex}
				onChangeIndex={setActiveTabIndex}
				backgroundColor={theme.colors.surface}
				tabHeaderStyle={{ backgroundColor: theme.colors.surface }}
				tabBarStyle={{
					width: "100%",
					maxWidth: theme.breakpoints.sm,
					alignSelf: "center",
				}}
			/>
		</AnimatedHeaderScreen>
	);
};
